import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { VideoPlayer } from "../components/VideoPlayer";
import { HLSLivePlayer } from "../components/HLSLivePlayer";
import { Radio, SkipForward, ChevronRight, Users } from "lucide-react";
import { useAnalytics, LiveViewerChart } from "../components/Analytics";
import { useI18n } from "../lib/i18n";
import { BreadcrumbJsonLd } from "../components/JsonLd";
import { ChannelZap } from "../components/ChannelZap";
import { ZapSoundToggle } from "../components/ZapSoundToggle";

export default function ChannelLive() {
  const { t } = useI18n();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/schedule/${id}`).then(r => { setData(r.data); setIdx(0); });
  }, [id]);

  const current = data?.playlist?.[idx];
  const schedule = useMemo(() => {
    if (!data) return [];
    const start = new Date();
    let t = new Date(start);
    return data.playlist.map((v, i) => {
      const label = t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      t = new Date(t.getTime() + (v.duration || 8 * 60) * 1000);
      return { ...v, timeLabel: label, index: i };
    });
  }, [data]);

  const next = () => setIdx((idx + 1) % (data?.playlist?.length || 1));
  const stats = useAnalytics(data?.channel?.id);

  if (!data) return <div className="p-10 text-center text-[color:var(--text-muted)]">{t("live.tuning")}</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6 grid lg:grid-cols-[1fr_380px] gap-6" data-testid="channel-live-page">
      <BreadcrumbJsonLd items={[
        { name: "Accueil", path: "/" },
        { name: "Chaînes", path: "/channels" },
        { name: data.channel.name, path: `/channel/${data.channel.id}` },
      ]} />
      <nav className="lg:col-span-2 flex items-center gap-1.5 text-xs uppercase tracking-widest text-[color:var(--text-muted)] -mt-2 mb-1" data-testid="breadcrumb">
        <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/channels" className="hover:text-white transition-colors">Chaînes</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-white">{data.channel.name}</span>
      </nav>
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span className="w-3 h-3 rounded-full" style={{ background: data.channel.color }} />
          <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)]">{t("live.channel")}</span>
          <span className="font-display text-3xl">{data.channel.name}</span>
          <ZapSoundToggle />
          <span className="ml-auto flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--accent)]"><span className="live-dot" /> {t("live.live")}</span>
        </div>
        <div className="crt-tv">
          <span className="crt-tv-label">{data.channel.name.toUpperCase()}</span>
          <span className="crt-knob l" />
          <span className="crt-knob r" />
          <div className="crt-screen">
            <ChannelZap zapKey={data.channel.id} name={data.channel.name} number={(data.channel.order ?? 0) + 1} color={data.channel.color} />
            {data.channel.live_active && data.channel.live_hls_url ? (
              <HLSLivePlayer
                src={data.channel.live_hls_url}
                poster={current?.thumbnail}
                channelName={data.channel.name}
                channelColor={data.channel.color}
              />
            ) : (
              <VideoPlayer video={current} autoplay={true} onEnded={next} />
            )}
          </div>
        </div>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl">{current?.title}</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-1 max-w-2xl">{current?.description || data.channel.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to={`/party?channel=${data.channel.id}`} data-testid="watch-together-btn" className="inline-flex items-center gap-2 border border-[color:var(--accent)]/50 text-[color:var(--accent)] hover:bg-[color:var(--accent)] hover:text-black px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors">
              <Users className="w-4 h-4" /> Regarder ensemble
            </Link>
            <button onClick={next} data-testid="next-video-btn" className="shrink-0 inline-flex items-center gap-2 border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-colors">
              <SkipForward className="w-4 h-4" /> {t("live.next")}
            </button>
          </div>
        </div>
      </div>

      <aside className="space-y-4 h-fit lg:sticky lg:top-20">
        <LiveViewerChart stats={stats} />
        <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="w-4 h-4 text-[color:var(--accent)]" />
          <h2 className="font-display text-xl">{t("live.upnext")}</h2>
        </div>
        <ol className="space-y-1 max-h-[70vh] overflow-y-auto">
          {schedule.map((v) => (
            <li key={v.id}>
              <button
                data-testid={`schedule-item-${v.index}`}
                onClick={() => setIdx(v.index)}
                className={`w-full text-left flex gap-3 p-2 rounded-lg transition ${v.index === idx ? 'bg-[color:var(--bg-elev-2)] border border-[color:var(--accent)]' : 'hover:bg-[color:var(--bg-elev-2)] border border-transparent'}`}
              >
                <span className="font-mono text-xs text-[color:var(--text-muted)] w-12 pt-1">{v.timeLabel}</span>
                <img src={v.thumbnail} alt="" className="w-20 h-12 object-cover rounded" />
                <span className="flex-1">
                  <span className="text-sm line-clamp-2">{v.title}</span>
                  {v.index === idx && <span className="text-[10px] uppercase tracking-wider text-[color:var(--accent)] mt-1 block">{t("live.now")}</span>}
                </span>
              </button>
            </li>
          ))}
        </ol>
        <Link to="/channels" className="block mt-4 text-center text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-white transition-colors">{t("live.back")}</Link>
        </div>
      </aside>
    </div>
  );
}
